import { useState } from "react";
import { Calendar, X, Trash2, Save } from "lucide-react";

/**
 * Inline date picker panel for setting/editing an evidence item's expiry date.
 * Renders below the evidence item card — not a modal.
 */
export default function ExpiryDatePicker({
  item,
  deadline,      // existing deadline record or null
  onSave,        // (data) => Promise<void> — data: { expiryDate, reminderOption, notes }
  onRemove,      // () => Promise<void>
  onClose,       // () => void
  themeMode,
}) {
  const isLight = themeMode === "clinical_light";
  const t = {
    bg:       isLight ? "#FFFFFF"  : "#162e4a",
    border:   isLight ? "#E2E8F0"  : "#213045",
    text:     isLight ? "#1F2D3D"  : "#F2F4F7",
    muted:    isLight ? "#64748B"  : "#7a9ab8",
    inputBg:  isLight ? "#F8FAFC"  : "#1b3355",
    inputBrd: isLight ? "#CBD5E1"  : "#213045",
    labelCol: isLight ? "#374151"  : "#94a3b8",
  };

  const [expiryDate, setExpiryDate] = useState(deadline?.expiry_date || "");
  const [reminderOption, setReminderOption] = useState(
    deadline ? inferReminderOption(deadline) : "all"
  );
  const [notes, setNotes] = useState(deadline?.notes || "");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleSave = async () => {
    if (!expiryDate) return;
    setSaving(true);
    try {
      await onSave({ expiryDate, reminderOption, notes: notes.trim() });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await onRemove();
      onClose();
    } finally {
      setRemoving(false);
    }
  };

  const inp = {
    width: "100%",
    background: t.inputBg,
    border: `1px solid ${t.inputBrd}`,
    borderRadius: 7,
    color: t.text,
    fontFamily: "inherit",
    fontSize: 12.5,
    padding: "7px 10px",
    outline: "none",
  };

  return (
    <div style={{
      background: t.bg,
      border: `1px solid ${t.border}`,
      borderRadius: "0 0 10px 10px",
      borderTop: "none",
      padding: "12px 14px 14px",
      marginTop: -1,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <Calendar size={12} style={{ color: "#2FAF9F", flexShrink: 0 }} />
        <span style={{ fontSize: 11.5, fontWeight: 700, color: t.text }}>Set expiry date</span>
        <span style={{ fontSize: 10.5, color: t.muted, marginLeft: 2 }}>— {item.label}</span>
        <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: t.muted, padding: 2, display: "flex", alignItems: "center", borderRadius: 4 }}>
          <X size={13} />
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        {/* Date input */}
        <div>
          <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: t.labelCol, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 4 }}>
            Certificate / training expiry date
          </label>
          <input
            type="date"
            value={expiryDate}
            onChange={e => setExpiryDate(e.target.value)}
            style={inp}
          />
        </div>

        {/* Reminder dropdown */}
        <div>
          <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: t.labelCol, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 4 }}>
            Renewal reminder
          </label>
          <select
            value={reminderOption}
            onChange={e => setReminderOption(e.target.value)}
            style={{ ...inp, cursor: "pointer" }}
          >
            <option value="all">All three (12w, 6w, 2w)</option>
            <option value="12w">12 weeks before</option>
            <option value="6w">6 weeks before</option>
            <option value="2w">2 weeks before</option>
          </select>
        </div>
      </div>

      {/* Notes */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: t.labelCol, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 4 }}>
          Notes (optional)
        </label>
        <input
          type="text"
          placeholder="e.g. Book via NHS England e-learning"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          style={inp}
        />
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          onClick={handleSave}
          disabled={!expiryDate || saving}
          style={{
            padding: "7px 16px",
            borderRadius: 7,
            border: "none",
            background: !expiryDate || saving ? (isLight ? "#CBD5E1" : "#1b3355") : "#2FAF9F",
            color: !expiryDate || saving ? t.muted : "#fff",
            cursor: !expiryDate || saving ? "not-allowed" : "pointer",
            fontSize: 12,
            fontWeight: 700,
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            gap: 5,
            transition: "background .15s",
          }}
        >
          <Save size={11} />
          {saving ? "Saving…" : deadline ? "Update" : "Save"}
        </button>

        {deadline && (
          <button
            onClick={handleRemove}
            disabled={removing}
            style={{
              padding: "7px 12px",
              borderRadius: 7,
              border: `1px solid rgba(239,68,68,.3)`,
              background: "rgba(239,68,68,.07)",
              color: "#f87171",
              cursor: removing ? "not-allowed" : "pointer",
              fontSize: 12,
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: 5,
              opacity: removing ? 0.6 : 1,
            }}
          >
            <Trash2 size={10} />
            {removing ? "Removing…" : "Remove"}
          </button>
        )}

        <button
          onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer", color: t.muted, fontSize: 12, fontFamily: "inherit", padding: "7px 8px" }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function inferReminderOption(deadline) {
  if (!deadline) return "all";
  const has12 = deadline.reminder_12w_date && !deadline.reminder_12w_sent;
  const has6  = deadline.reminder_6w_date  && !deadline.reminder_6w_sent;
  const has2  = deadline.reminder_2w_date  && !deadline.reminder_2w_sent;
  if (has12 && has6 && has2) return "all";
  if (has12) return "12w";
  if (has6)  return "6w";
  if (has2)  return "2w";
  return "all";
}
